import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ChatWidget from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PURPOSES = ["Tourism", "Business", "Transit", "Student", "Work"];

export default function Checker() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [nationality, setNationality] = useState("");
  const [destination, setDestination] = useState("");
  const [purpose, setPurpose] = useState("Tourism");
  const [duration, setDuration] = useState(14);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries));
  }, []);

  const handleCheck = async () => {
    if (!nationality || !destination) return;
    setLoading(true);
    try {
      const { data } = await api.post("/check-visa", {
        nationality,
        destination,
        purpose: purpose.toLowerCase(),
        duration_days: Number(duration) || 14,
      });
      navigate(`/results/${data.id}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grid">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:py-16">
        <h1 className="font-heading text-3xl font-bold sm:text-4xl">Visa Requirement Checker</h1>
        <p className="mt-2 text-slate-600">
          Get personalized, AI-powered visa and document guidance in seconds.
        </p>

        <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-lg sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                Your nationality
              </Label>
              <Select value={nationality} onValueChange={setNationality}>
                <SelectTrigger data-testid="checker-nationality" className="h-12">
                  <SelectValue placeholder="Passport country" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                Destination
              </Label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger data-testid="checker-destination" className="h-12">
                  <SelectValue placeholder="Where are you going?" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
              Purpose
            </Label>
            <div className="flex flex-wrap gap-2">
              {PURPOSES.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`checker-purpose-${p.toLowerCase()}`}
                  onClick={() => setPurpose(p)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    purpose === p
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="duration" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
              Trip duration (days)
            </Label>
            <Input
              id="duration"
              data-testid="checker-duration"
              type="number"
              min={1}
              max={365}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="h-12"
            />
          </div>

          <Button
            data-testid="checker-submit"
            onClick={handleCheck}
            disabled={loading || !nationality || !destination}
            className="mt-6 h-12 w-full bg-slate-900 text-base font-semibold hover:bg-slate-800"
          >
            {loading ? "Analyzing with AI..." : "Analyze my trip"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </main>
      <ChatWidget />
    </div>
  );
}
